package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.home.styles.ClinicalAccreditationLogoStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.Div

@Composable
private fun Modifier.staggeredPopIn(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.5f).transition(
            Transition.of(
                "opacity",
                duration = 0.6.s,
                timingFunction = TransitionTimingFunction.EaseOut,
                delay = delayMs.ms
            ),
            Transition.of(
                "transform",
                duration = 0.6.s,
                timingFunction = TransitionTimingFunction.EaseOut,
                delay = delayMs.ms
            )
        )
}

@Composable
fun HomeClinicalAccreditationSection(data: HomeClinicalAccreditationSectionData = defaultHomeClinicalAccreditationSectionData) {
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-accreditation-section"

    var isVisible by remember { mutableStateOf(false) }

    if (data.accreditations.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(attrs = Modifier.fillMaxWidth().toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element)
                    onDispose { observer.disconnect() }
                }
            }) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 25.px).staggeredPopIn(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).display(DisplayStyle.Flex)
                            .flexWrap(FlexWrap.Wrap).justifyContent(JustifyContent.Center).alignItems(AlignItems.Center)
                    ) {
                        data.accreditations.forEachIndexed { index, accData ->
                            key(accData.src) {
                                // RESOLVED: Passing the entire accData object instead of just the src string
                                ClinicalAccreditationLogo(
                                    data = accData,
                                    modifier = Modifier.weight(1f).staggeredPopIn(isVisible, 200 + (index * 150))
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// RESOLVED: Updated to accept AccreditationData and render the region text below the image
@Composable
private fun ClinicalAccreditationLogo(data: AccreditationData, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.minWidth(250.px).padding(10.px), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (data.src.isNotBlank()) {
            Image(src = data.src, modifier = ClinicalAccreditationLogoStyle.toModifier().unextractable())
        }
        SpanText(
            text = data.regionName,
            modifier = Modifier.margin(top = 8.px).fontSize(20.px).fontWeight(FontWeight.Bold)
                .color(Colors.DarkSlateGray).textAlign(TextAlign.Center)
        )
    }
}

// CMS Data Model
/**
 * CMS IMAGE UPLOAD SPECS:
 * @field src:
 * - Aspect Ratio: Flexible (Horizontal/Landscape preferred).
 * - Recommended Dimensions: Max width 300px, max height 100px.
 * - Format: SVG or transparent PNG ONLY. No white backgrounds.
 * - Max File Size: 100KB.
 * - Behavior: Will scale to fit within a 300px wide container to ensure responsiveness.
 */
data class AccreditationData(val src: String, val regionName: String)

data class HomeClinicalAccreditationSectionData(
    val sectionId: String? = null, val sectionHeader: String, val accreditations: List<AccreditationData>
)

val defaultHomeClinicalAccreditationSectionData = HomeClinicalAccreditationSectionData(
    sectionId = "home-accreditation-section",
    sectionHeader = "CLINICIAN TRUSTED AND REGULATORY APPROVED",
    accreditations = listOf(
        AccreditationData(Res.Images.Home.Accreditations.HEALTH_CANADA, "Canada"),
        AccreditationData(Res.Images.Home.Accreditations.FDA_APPROVED, "United States"),
        AccreditationData(Res.Images.Home.Accreditations.CDSCO, "India"),
        AccreditationData(Res.Images.Home.Accreditations.HSA, "Singapore")
    )
)